import { clusterForEnvironment } from './environments'

/** Resource annotation: the `host` label of the droplets node_exporter scrape job. */
export const PROMETHEUS_HOST_ANNOTATION = 'deepgaming.eu/prometheus-host'

/** Cluster-level PromQL for the Fleet page; every series is in the topology hub. */
export function fleetQueries(env: string) {
  const c = `cluster="${clusterForEnvironment(env)}"`
  const ns = `${c},namespace="deepgaming"`
  return {
    nodesByPool: `count by (pool) (label_replace(kube_node_info{${c}}, "pool", "$1", "node", "(.+)-[^-]+"))`,
    replicasDesired: `sum(kube_deployment_spec_replicas{${ns}}) + sum(kube_statefulset_replicas{${ns}} or vector(0))`,
    replicasAvailable: `sum(kube_deployment_status_replicas_available{${ns}}) + sum(kube_statefulset_status_replicas_ready{${ns}} or vector(0))`,
    pendingPods: `sum(kube_pod_status_phase{${ns},phase="Pending"})`,
    waitingPods: `sum(kube_pod_container_status_waiting_reason{${ns},reason=~"CrashLoopBackOff|ImagePullBackOff|ErrImagePull|CreateContainerConfigError"})`,
    restarts1h: `sum(increase(kube_pod_container_status_restarts_total{${ns}}[1h]))`,
    oom1h: `sum(increase(container_oom_events_total{${ns}}[1h]))`,
    cpuCores: `sum(rate(container_cpu_usage_seconds_total{${ns}}[5m]))`,
    cpuRequestCores: `sum(kube_pod_container_resource_requests{${ns},resource="cpu"})`,
    memoryBytes: `sum(container_memory_working_set_bytes{${ns}})`,
    memoryRequestBytes: `sum(kube_pod_container_resource_requests{${ns},resource="memory"})`,
    alertsBySeverity: `count by (severity) (ALERTS{${c},alertstate="firing",alertname!~"Watchdog|InfoInhibitor|DeadMansSwitch"})`,
    trivyBySeverity: `sum by (severity) (trivy_image_vulnerabilities{${ns},severity=~"Critical|High"})`,
    certsExpiringSoon: `count(certmanager_certificate_expiration_timestamp_seconds{${ns}} - time() < 21 * 86400)`,
    topCpuPods: `topk(5, sum by (pod) (rate(container_cpu_usage_seconds_total{${ns}}[5m])))`,
    topMemoryPods: `topk(5, sum by (pod) (container_memory_working_set_bytes{${ns}}))`,
  }
}

/** node_exporter on the droplets, forwarded to the hub as job="droplets" with a `host` label. */
export function dropletQueries(host?: string) {
  const sel = host ? `{job="droplets",host="${host}"}` : `{job="droplets"}`
  const fs = (m: string) => (host ? `${m}{job="droplets",host="${host}",mountpoint="/"}` : `${m}{job="droplets",mountpoint="/"}`)
  return {
    up: `up${sel}`,
    cpuBusy: `100 * (1 - avg by (host) (rate(node_cpu_seconds_total${host ? `{job="droplets",host="${host}",mode="idle"}` : `{job="droplets",mode="idle"}`}[5m])))`,
    memoryUsedPercent: `100 * (1 - node_memory_MemAvailable_bytes${sel} / node_memory_MemTotal_bytes${sel})`,
    memoryTotalBytes: `node_memory_MemTotal_bytes${sel}`,
    diskUsedPercent: `100 * (1 - ${fs('node_filesystem_avail_bytes')} / ${fs('node_filesystem_size_bytes')})`,
    diskTotalBytes: fs('node_filesystem_size_bytes'),
    load1: `node_load1${sel}`,
    cores: `count by (host) (node_cpu_seconds_total${host ? `{job="droplets",host="${host}",mode="idle"}` : `{job="droplets",mode="idle"}`})`,
    uptimeSeconds: `time() - node_boot_time_seconds${sel}`,
  }
}

export const DROPLETS_DASHBOARD = { uid: 'droplets', slug: 'droplets' }

/** Panel ids of global/grafana/droplets-dashboard.json in dg-k8s-infrastructure (1 is the table). */
export const DROPLET_PANELS = {
  cpuBusy: 2,
  cpuIowait: 3,
  memory: 4,
  disk: 5,
  diskIo: 6,
  network: 7,
} as const

export function grafanaDropletsUrl(baseUrl: string, host?: string): string {
  const q = new URLSearchParams({ orgId: '1' })
  if (host) q.set('var-host', host)
  const { uid, slug } = DROPLETS_DASHBOARD
  return `${baseUrl.replace(/\/+$/, '')}/d/${uid}/${slug}?${q}`
}

export function grafanaDropletPanelUrl(opts: {
  baseUrl: string
  host: string
  panelId: number
  theme: 'light' | 'dark'
  from?: string
  to?: string
}): string {
  const q = new URLSearchParams({
    orgId: '1',
    'var-host': opts.host,
    panelId: String(opts.panelId),
    theme: opts.theme,
    from: opts.from ?? 'now-6h',
    to: opts.to ?? 'now',
  })
  const { uid, slug } = DROPLETS_DASHBOARD
  return `${opts.baseUrl.replace(/\/+$/, '')}/d-solo/${uid}/${slug}?${q}`
}

export function formatUptime(seconds?: number): string {
  if (seconds === undefined) return '-'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  return d > 0 ? `${d}d ${h}h` : `${h}h`
}
